import { type TypedDocumentNode, useApolloClient, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import {
  initialPagedGridInput,
  PAGED_LIST_FETCH_POLICY,
  type PagedGridQueryState,
  usePreloadedServerGridQuery,
} from 'src/modules/grids';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import type {
  ExpenseTypeLedgerAccountsQuery,
  ExpenseTypeLedgerAccountsQueryVariables,
  ExpenseTypeNameTakenQuery,
  ExpenseTypeNameTakenQueryVariables,
  ExpenseTypePageQuery,
  ExpenseTypePageQueryVariables,
} from 'types/graphql';

/** Where the Charge Types grid persists its layout. v2 held LOCAL sort/filter state on the relation columns. */
export const EXPENSE_TYPES_GRID_STORAGE_KEY = 'expenseTypes_v3';

/** The Charge Types grid's bounded read (TAS-3660 P7 BZ74): one server page per request. */
export const EXPENSE_TYPE_PAGE_QUERY: TypedDocumentNode<ExpenseTypePageQuery, ExpenseTypePageQueryVariables> = gql`
  query ExpenseTypePageQuery($input: StandardPageInput!) {
    expenseTypePage(input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          disabled
          payablesAccount {
            id
            accountName
          }
          receivablesAccount {
            id
            accountName
          }
          accountLevels
          isActivePayable
          isActiveReceivable
        }
      }
    }
  }
`;

/** The New/Edit modal's account choices (moved out of the old ExpenseTypesQuery); read when the modal first opens. */
export const EXPENSE_TYPE_LEDGER_ACCOUNTS_QUERY: TypedDocumentNode<
  ExpenseTypeLedgerAccountsQuery,
  ExpenseTypeLedgerAccountsQueryVariables
> = gql`
  query ExpenseTypeLedgerAccountsQuery {
    incomeAccounts: ledgerAccountDetails(accountTypes: [Income, OtherIncome]) {
      id
      accountName
      accountNumber
      accountType
    }
    payableAccounts: ledgerAccountDetails(accountTypes: [DirectCosts, IndirectCosts, OtherCosts]) {
      id
      accountName
      accountNumber
      accountType
    }
  }
`;

/** The duplicate-name check before a save: at most two live rows carrying exactly that name. */
const EXPENSE_TYPE_NAME_TAKEN_QUERY: TypedDocumentNode<ExpenseTypeNameTakenQuery, ExpenseTypeNameTakenQueryVariables> =
  gql`
  query ExpenseTypeNameTakenQuery($input: StandardPageInput!) {
    expenseTypePage(input: $input) {
      edges {
        node {
          id
        }
      }
    }
  }
`;

export type ExpenseTypeRow = NonNullable<
  NonNullable<NonNullable<ExpenseTypePageQuery['expenseTypePage']['edges']>[number]>['node']
>;

export function useExpenseTypes(gridState: PagedGridQueryState) {
  return usePreloadedServerGridQuery({
    queryKey: 'expenseTypePage',
    query: EXPENSE_TYPE_PAGE_QUERY,
    gridState,
    label: 'charge types',
  });
}

export function useExpenseTypeLedgerAccounts({ skip }: { skip: boolean }) {
  const { data } = useQuery(EXPENSE_TYPE_LEDGER_ACCOUNTS_QUERY, { skip });
  return { incomeAccounts: data?.incomeAccounts ?? [], payableAccounts: data?.payableAccounts ?? [] };
}

/**
 * Whether another live charge type already has this exact name. `createExpenseType` upserts by name, so
 * without this check a duplicate silently overwrites the existing type; the grid no longer holds every row.
 */
export function useExpenseTypeNameTaken() {
  const client = useApolloClient();
  return useCallback(
    async (name: string, exceptId?: number) => {
      const { data } = await client.query({
        query: EXPENSE_TYPE_NAME_TAKEN_QUERY,
        variables: { input: { first: 2, gridFilters: [{ field: 'name', where: [{ equals: name }] }] } },
        fetchPolicy: 'network-only',
      });
      return (data.expenseTypePage.edges ?? []).some((edge) => edge?.node && edge.node.id !== exceptId);
    },
    [client]
  );
}

/** The grid's first page as it will ask for it; the Charge Types tab preloads it on intent. */
export function preloadExpenseTypesGrid(context: AppRouterContext) {
  const input = initialPagedGridInput(EXPENSE_TYPES_GRID_STORAGE_KEY);
  return preloadRouteQuery(context, EXPENSE_TYPE_PAGE_QUERY, { input }, { fetchPolicy: PAGED_LIST_FETCH_POLICY });
}
